import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import CommonSelect from "../../core/common/commonSelect";
import Table from "../../core/common/dataTable/index";
import PredefinedDateRanges from "../../core/common/datePicker";
import TooltipOption from "../../core/common/tooltipOption";
import { selectSystemsForCurrentBranch } from "../../core/data/redux/slice/systemsSlice";
import {
  cancelInvoice,
  exportInvoicesToExcel,
  FeeBreakdownItem,
  getAllFeesInvoices,
  getAllInvoicesForPrint,
  getInvoiceSummary,
  MonthBreakdownItem,
} from "../../service/fee-invoices";
import { all_routes } from "../router/all_routes";
import { toast } from "react-toastify";
import { SORT_OPTIONS } from "../../core/common/selectoption/selectoption";
import { selectCurrentSession } from "../../core/data/redux/slice/sessionsSlice";
import { Class } from "../../service/classes";
import { FeeType } from "../../service/discount";
import { FEE_GROUP, getAllFeeGroups } from "../../service/fee-group";
import { Student } from "../../service/student";
import { AdmissionData } from "../admission/admission/admission-list";
import { ConfirmationModal } from "../../components";
import InvoicePrintModal from "../peoples/students/student-details/invoicePrintModal";
import { useReactToPrint } from "react-to-print";
import { Section } from "../../service/class-room";
import { selectUser } from "../../core/data/redux/slice/user";
import { SYSTEM_ROLES } from "../../constant";
import { isSuperAdmin } from "../../utils/rbacHelper";
import InvoicesPrintModal from "./invoicesPrintModal";
import { computeCashierReportTotals, formatMonth } from "./cashierReportShared";

export type InvoiceTableData = {
  _id: string;
  invoiceNumber: string;
  status: "paid" | "unpaid" | "partial" | "cancelled";
  total: number;
  subTotal: number;
  fine: number;
  discount: {
    profile: number;
    manual: number;
  };
  invoiceItems: {
    _id: string;
    amount: number;
    discount: number;
    description: string;
    month?: string;

    feeType: FeeType;
    feeGroup: FEE_GROUP;
  }[];

  student?: Student;
  admission?: AdmissionData;

  class: Class;
  section?: Section;

  branch?: { _id?: string; name?: string; schoolName?: string } | string;
  session: string;
  system: string;
  enrollment?: string;

  isDeleted: boolean;
  createdBy: string | null;

  createdAt: string;
  updatedAt: string;

  notes?: string;
};

interface PaginationDataType {
  currentPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  total: number;
  totalPages: number;
}

interface InvoiceSummary {
  totalCollected: number;
  totalDiscount: number;
  totalFine: number;
  totalOnline: number;
  totalCash: number;
  totalPending?: number;
  totalDue?: number;
  feeBreakdown: FeeBreakdownItem[];
  monthBreakdownData: MonthBreakdownItem[];
}

const DEFAULT_SUMMARY = {
  totalCollected: 0,
  totalDiscount: 0,
  totalFine: 0,
  totalOnline: 0,
  totalCash: 0,
  feeBreakdown: [],
  monthBreakdownData: [],
};

const AccountsInvoices = () => {
  const routes = all_routes;
  const systems = useSelector(selectSystemsForCurrentBranch);
  const currentSession = useSelector(selectCurrentSession);
  const user = useSelector(selectUser);

  const [invoices, setInvoices] = useState<InvoiceTableData[]>([]);
  // Was: true, with a fetch firing automatically the instant the page
  // opened (same slow "Last 30 Days" query every single visit, whether or
  // not anyone needed it). Now the page opens empty -- loading only starts
  // once a real fetch is in flight, which happens the first time a filter
  // is touched or Search is clicked (see the mount-skip effect below).
  const [loading, setLoading] = useState(false);
  // Flips true on the first real fetch -- used only to show "change a
  // filter or click Search" instead of "no invoices" before the page has
  // ever actually searched for anything.
  const [hasSearched, setHasSearched] = useState(false);
  // True whenever the filters on screen don't match what was last
  // actually searched -- drives the Search button's enabled state. Starts
  // true so the very first click (with nothing touched yet) still works.
  // Filter changes only set this; they no longer fetch anything
  // themselves -- only Search (or changing page, which is navigation
  // within an already-run search, not a new filter) does that.
  const [hasPendingChanges, setHasPendingChanges] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [deleteInvoiceId, setDeleteInvoiceId] = useState("");
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] =
    useState<InvoiceTableData | null>();
  const [paginationData, setPaginationData] = useState<PaginationDataType>();
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
  });

  const [feeGroups, setFeeGroups] = useState<FEE_GROUP[]>([]);

  const [appliedFilters, setAppliedFilters] = useState<{
    class?: string;
    status?: string;
    system?: string;
    session?: string;
    branch?: string;
    startDate?: string;
    endDate?: string;
    feeGroup?: string;
  }>({
    system: systems[0]?._id,
    session: currentSession?._id,
    startDate: dayjs().subtract(29, "days").toISOString(),
    endDate: dayjs().toISOString(),
  });

  const [summary, setSummary] = useState<InvoiceSummary>(DEFAULT_SUMMARY);
  // Full (unpaginated) paid invoices for the current filters — feeds the
  // Tuition Fee Breakdown card via the same classifyTuitionItem logic the
  // Cashier Report uses, so "Tuition" there always includes STEM/STEAM too
  // and the two screens can't drift apart on what counts as tuition.
  const [tuitionBreakdownInvoices, setTuitionBreakdownInvoices] = useState<
    InvoiceTableData[]
  >([]);

  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0]);

  const [cancellingInvoice, setCancellingInvoice] = useState(false);

  const [printLoading, setPrintLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [allInvoicesForPrint, setAllInvoicesForPrint] = useState<
    InvoiceTableData[]
  >([]);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState<"pdf" | "excel">("pdf");

  // fee breakdown and month breakdown now come from summary

  // Accepts an explicit pagination override so page-navigation (Prev/Next,
  // page size) can fetch the NEW page immediately without waiting for a
  // re-render to pick up state set a moment earlier (setPagination is
  // async -- reading `pagination` from closure here would still see the
  // old page on the very same call that just changed it).
  const fetchAllData = useCallback(async (paginationOverride?: { page: number; limit: number }) => {
    const effectivePagination = paginationOverride || pagination;
    setLoading(true);
    setHasSearched(true);
    setHasPendingChanges(false);

    try {
      const [invoicesResponse, summaryData, tuitionInvoicesResponse] =
        await Promise.all([
          getAllFeesInvoices({
            page: effectivePagination.page,
            limit: effectivePagination.limit,
            searchText: searchText,
            status: appliedFilters?.status,
            class: appliedFilters?.class,
            system: appliedFilters?.system,
            session: appliedFilters?.session,
            branch: appliedFilters?.branch,
            feeGroup: appliedFilters?.feeGroup,
            startDate: appliedFilters?.startDate,
            endDate: appliedFilters?.endDate,
            sortBy: sortBy.value.sortBy,
            sortOrder: sortBy.value.sortOrder,
          }),
          getInvoiceSummary({
            sessionId: currentSession?._id,
            status: appliedFilters?.status,
            class: appliedFilters?.class,
            system: appliedFilters?.system,
            branch: appliedFilters?.branch,
            dateFrom: appliedFilters?.startDate,
            dateTo: appliedFilters?.endDate,
            feeGroup: appliedFilters?.feeGroup,
          } as any),
          // Tuition Fee Breakdown card — always "paid", like the Fees
          // Breakdown card above it, regardless of the status filter.
          // fields:"summary" -- this card (computeCashierReportTotals's
          // tuitionBreakdown) only ever reads invoiceItems-level numbers,
          // never student/class/admission, so it skips those joins
          // entirely. Verified against live data to produce byte-identical
          // breakdown numbers to the full fetch before shipping.
          getAllInvoicesForPrint({
            status: "paid",
            class: appliedFilters?.class,
            system: appliedFilters?.system,
            session: appliedFilters?.session,
            branch: appliedFilters?.branch,
            feeGroup: appliedFilters?.feeGroup,
            startDate: appliedFilters?.startDate,
            endDate: appliedFilters?.endDate,
            fields: "summary",
          }),
        ]);

      if (invoicesResponse.success) {
        setInvoices(invoicesResponse.data);

        setPaginationData({
          currentPage: invoicesResponse.pagination.currentPage,
          hasNextPage: invoicesResponse.pagination.hasNextPage,
          hasPrevPage: invoicesResponse.pagination.hasPrevPage,
          limit: invoicesResponse.pagination.limit,
          total: invoicesResponse.pagination.total,
          totalPages: invoicesResponse.pagination.totalPages,
        });

        setSummary(summaryData);
      }

      setTuitionBreakdownInvoices(
        tuitionInvoicesResponse.success ? tuitionInvoicesResponse.data : []
      );
    } catch (error) {
      console.error("Error fetching data:", error);
      setInvoices([]);
      setSummary(DEFAULT_SUMMARY);
      setTuitionBreakdownInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [pagination, appliedFilters, sortBy, searchText, currentSession?._id]);

  const tuitionTotals = useMemo(
    () => computeCashierReportTotals(tuitionBreakdownInvoices),
    [tuitionBreakdownInvoices]
  );

  const handleCancelInvoice = async () => {
    setCancellingInvoice(true);
    try {
      await cancelInvoice(deleteInvoiceId);
      await fetchAllData();
      toast.success("Invoice cancelled successfully!");
    } catch (error) {
      console.error("Error cancelling invoice:", error);
      toast.error("Failed to cancel invoice. Please try again.");
    } finally {
      setCancellingInvoice(false);
    }
  };

  const handlePrintAllInvoices = async () => {
    setPrintLoading(true);
    setExportType("pdf");
    try {
      const response = await getAllInvoicesForPrint({
        searchText: searchText,
        status: appliedFilters?.status,
        class: appliedFilters?.class,
        system: appliedFilters?.system,
        session: appliedFilters?.session,
        branch: appliedFilters?.branch,
        feeGroup: appliedFilters?.feeGroup,
        startDate: appliedFilters?.startDate,
        endDate: appliedFilters?.endDate,
      });

      if (response.success) {
        setAllInvoicesForPrint(response.data);
        setShowPrintModal(true);
      }
    } catch (error) {
      console.error("Error fetching invoices for print:", error);
      toast.error("Failed to load invoices for printing");
    } finally {
      setPrintLoading(false);
    }
  };

  useEffect(() => {
    setAppliedFilters((prev) => ({
      ...prev,
      session: currentSession?._id,
    }));
  }, [currentSession?._id]);

  // Was: an effect that auto-fetched the instant the page opened, and
  // again on every single filter change -- the slow "Last 30 Days" query
  // fired automatically every visit, and the Search button never actually
  // did anything a filter change hadn't already triggered. Now nothing
  // fetches on its own: filter handlers above only mark hasPendingChanges,
  // and the only things that ever call fetchAllData are an explicit
  // Search click and real page navigation (handleChange).

  useEffect(() => {
    getAllFeeGroups({ limit: 100 })
      .then((res) => setFeeGroups(res.data))
      .catch(() => {});
  }, []);

  const handleExportInvoices = async (type: "pdf" | "excel") => {
    setExportLoading(true);
    setExportType(type);
    try {
      const response = await exportInvoicesToExcel({
        searchText: searchText,
        status: appliedFilters?.status,
        class: appliedFilters?.class,
        system: appliedFilters?.system,
        session: appliedFilters?.session,
        branch: appliedFilters?.branch,
        feeGroup: appliedFilters?.feeGroup,
        startDate: appliedFilters?.startDate,
        endDate: appliedFilters?.endDate,
      });

      if (response.success) {
        setAllInvoicesForPrint(response.data);
        if (type === "pdf") {
          setShowPrintModal(true);
        } else {
          setShowExportModal(true);
        }
      }
    } catch (error) {
      console.error(`Error fetching invoices for ${type}:`, error);
      toast.error(`Failed to load invoices for ${type}`);
    } finally {
      setExportLoading(false);
    }
  };

  // Page navigation within an already-run search -- stays live/immediate,
  // unlike the filters below, which only mark the Search button as
  // enabled and wait for an explicit click.
  const handleChange = (page: number, pageSize: number) => {
    const next = { limit: pageSize, page };
    setPagination(next);
    fetchAllData(next);
  };

  const handleSystemChange = (systemItem: any) => {
    const newSystemId = systemItem?._id;

    setHasPendingChanges(true);
    setAppliedFilters((prev) => ({
      ...prev,
      system: newSystemId,
    }));
  };

  const handleSortSelect = (item: {
    label: string;
    value: {
      sortBy: string;
      sortOrder: string;
    };
  }) => {
    setHasPendingChanges(true);
    setSortBy(item);
  };

  const handleRefresh = async () => {
    await fetchAllData();
  };

  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Fee-Receipt-${selectedInvoice?.invoiceNumber || ""}`,
    onAfterPrint: () => {
      setShowInvoiceModal(false);
      setSelectedInvoice(null);
    },
  });

  const handleTooltipPrint = () => {
    handlePrintAllInvoices();
  };

  const handleTooltipExportExcel = () => {
    handleExportInvoices("excel");
  };

  const handleCloseInvoiceModal = () => {
    setShowInvoiceModal(false);
    setSelectedInvoice(null);
  };

  const handlePrintInvoice = (invoice: InvoiceTableData) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const calculateTotalDiscount = (record: any) => {
    const manualDiscount = record.discount?.manual || 0;
    const profileDiscount = record.discount?.profile || 0;
    return manualDiscount + profileDiscount;
  };

  const calculateFinalAmount = (record: any) => {
    const subTotal = record.subTotal || 0;
    const fine = record.fine || 0;
    const totalDiscount = calculateTotalDiscount(record);
    return subTotal - totalDiscount + fine;
  };

  const columns = [
    {
      title: "Receipt No.",
      dataIndex: "invoiceNumber",
      render: (text: string, record: InvoiceTableData) => (
        <Link
          to="#"
          className="link-primary"
          onClick={() => handlePrintInvoice(record)}
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Student Name",
      dataIndex: "studentName",
      render: (_text: string, record: InvoiceTableData) => (
        <Link to="#" onClick={() => handlePrintInvoice(record)}>
          {record.student?.user
            ? `${record.student.user.firstName || ""} ${
                record.student.user.lastName || ""
              }`.trim()
            : record.admission?.firstName
            ? `${record.admission.firstName || ""} ${
                record.admission.lastName || ""
              }`.trim()
            : "N/A"}
        </Link>
      ),
    },
    {
      title: "Class",
      dataIndex: "className",
      render: (_text: string, record: InvoiceTableData) => (
        <span>{record.class?.name || "N/A"}</span>
      ),
    },
    {
      title: "Fees Group",
      dataIndex: "invoiceItems",
      render: (_: any, record: InvoiceTableData) => {
        const feeGroups = record?.invoiceItems?.map(
          (item: any) =>
            item?.feeGroup?.name || item?.feeGroupName || "Other Fee"
        );
        const uniqueFeeGroups = Array.from(new Set(feeGroups));
        return uniqueFeeGroups?.length > 0 ? uniqueFeeGroups.join(", ") : "-";
      },
    },
    {
      title: "Month",
      key: "month",
      render: (_: any, record: InvoiceTableData) => {
        const monthsAndTypes = record.invoiceItems
          ?.map((item: any) =>
            item.month && item.month !== "other"
              ? dayjs(item.month).format("MMMM YYYY")
              : item.feeName
          )
          .filter(Boolean);

        if (!monthsAndTypes || monthsAndTypes.length === 0) return "-";

        return monthsAndTypes.join(", ");
      },
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (createdAt: string) => new Date(createdAt).toLocaleDateString(),
    },
    {
      title: "Sub Total (RS)",
      dataIndex: "subTotal",
    },
    {
      title: "Discount (RS)",
      dataIndex: "discount",
      render: (discount: { manual: number; profile: number }) =>
        discount.manual + discount.profile,
    },
    {
      title: "Fine (RS)",
      dataIndex: "fine",
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Paid Amount (RS)",
      dataIndex: "total",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => {
        let badgeClass = "badge-soft-info";
        if (status === "paid") badgeClass = "badge-soft-success";
        if (status === "Overdue") badgeClass = "badge-soft-warning";
        if (status === "unpaid") badgeClass = "badge-soft-danger";

        return (
          <span
            className={`badge d-inline-flex align-items-center ${badgeClass}`}
          >
            <i className="ti ti-circle-filled fs-5 me-1" />
            {status}
          </span>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: any, record: InvoiceTableData) => {
        const isAdmissionInvoice = record.invoiceItems?.some(
          (item: any) =>
            item.feeGroup?.name?.toLowerCase() === "admission" ||
            item.name?.toLowerCase() === "admission"
        );

        // Admission invoices used to be blocked from cancellation entirely --
        // the backend cancel is just a status flip (no cascading writes to
        // the linked Admission/enrollment record), so owner/admin can now
        // cancel these too. Scoped to owner/admin specifically, not every
        // role with the general invoices.delete permission (showCancelBtn
        // below), since that's who this was asked for.
        const disabled =
          record.status === "cancelled" || (isAdmissionInvoice && !isSuperAdmin(user));

        const showCancelBtn =
          isSuperAdmin(user) ||
          user?.permissions?.includes("invoices.delete") ||
          user?.permissions?.includes("invoice.delete");

        return (
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary btn-sm ms-2"
              onClick={() => handlePrintInvoice(record)}
            >
              Print
            </button>
            {showCancelBtn && (
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => setDeleteInvoiceId(record._id)}
                disabled={disabled}
                data-bs-toggle="modal"
                data-bs-target="#delete-invoice-modal"
              >
                <i className="ti ti-trash me-1" />
                Cancel
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Invoices</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Finance & Accounts</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Invoices
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={handleRefresh}
                onExportPdf={handleTooltipPrint}
                onExportExcel={handleTooltipExportExcel}
              />
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Invoices List</h4>

              <div className="d-flex align-items-center flex-wrap">
                <div className="mb-3 me-2">
                  <PredefinedDateRanges
                    onDateSelect={({ startDate, endDate }) => {
                      setHasPendingChanges(true);
                      setAppliedFilters((prev) => ({
                        ...prev,
                        startDate,
                        endDate,
                      }));
                      setPagination((prev) => ({
                        page: 1,
                        limit: prev.limit,
                      }));
                    }}
                  />
                </div>
                {/* System Filter */}
                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={systems.find(
                      (sys: any) => sys._id === appliedFilters?.system
                    )}
                    getOptionValue={(item: any) => item._id}
                    getOptionLabel={(item: any) => item.name}
                    onChange={handleSystemChange}
                    placeholder="Select System"
                  />
                </div>

                {/* Fee Group Filter */}
                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={[{ _id: "", name: "All Fee Groups" }, ...feeGroups]}
                    value={
                      appliedFilters?.feeGroup
                        ? feeGroups.find(
                            (fg) => fg._id === appliedFilters.feeGroup
                          ) || null
                        : { _id: "", name: "All Fee Groups" }
                    }
                    getOptionValue={(item: any) => item._id}
                    getOptionLabel={(item: any) => item.name}
                    onChange={(item: any) => {
                      setHasPendingChanges(true);
                      setAppliedFilters((prev) => ({
                        ...prev,
                        feeGroup: item?._id || undefined,
                      }));
                      setPagination((prev) => ({ page: 1, limit: prev.limit }));
                    }}
                    placeholder="All Fee Groups"
                  />
                </div>

                <div className="mb-3 me-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => fetchAllData()}
                    disabled={loading || !hasPendingChanges}
                    title={
                      !hasPendingChanges
                        ? "No filter changes to search"
                        : undefined
                    }
                  >
                    <i className="ti ti-search me-1" />
                    {hasSearched ? "Search" : "Load Invoices"}
                  </button>
                </div>

                {/* Sort Dropdown */}
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {SORT_OPTIONS.map((item: any, index: number) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${
                            item.label === sortBy.label ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            {process.env.REACT_APP_SKIP_VISIBLE_FLAG && (
              <div className="card mb-4">
                <div className="card-header">
                  <h4 className="card-title mb-0">Fee Summary</h4>
                </div>
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading summary...</p>
                    </div>
                  ) : (
                    <div className="row">
                      <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-3">
                        <div className="card border hover-shadow">
                          <div className="card-body text-center py-4">
                            <div className="mb-3">
                              <i className="ti ti-cash-banknote text-primary fs-1"></i>
                            </div>
                            <h2 className="fw-bold mb-1 text-primary">
                              {summary.totalCollected.toLocaleString()}
                            </h2>
                            <p className="text-muted mb-0 fw-semibold">
                              Total Collected
                            </p>
                            <small className="text-muted">
                              All collected fees amount
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-3">
                        <div className="card border shadow-sm hover-shadow">
                          <div className="card-body text-center py-4">
                            <div className="mb-3">
                              <i className="ti ti-discount text-success fs-1"></i>
                            </div>
                            <h2 className="fw-bold mb-1 text-success">
                              {summary.totalDiscount.toLocaleString()}
                            </h2>
                            <p className="text-muted mb-0 fw-semibold">
                              Total Discount
                            </p>
                            <small className="text-muted">
                              Discount given on invoices
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-3">
                        <div className="card border shadow-sm hover-shadow">
                          <div className="card-body text-center py-4">
                            <div className="mb-3">
                              <i className="ti ti-alert-circle text-secondary fs-1"></i>
                            </div>
                            <h2 className="fw-bold mb-1 text-secondary">
                              {summary.totalFine.toLocaleString()}
                            </h2>
                            <p className="text-muted mb-0 fw-semibold">
                              Total Fine
                            </p>
                            <small className="text-muted">
                              Late payment fines collected
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-3">
                        <div className="card border shadow-sm hover-shadow">
                          <div className="card-body text-center py-4">
                            <div className="mb-3">
                              <i className="ti ti-device-mobile text-info fs-1"></i>
                            </div>
                            <h2 className="fw-bold mb-1 text-info">
                              {summary.totalOnline.toLocaleString()}
                            </h2>
                            <p className="text-muted mb-0 fw-semibold">
                              Total Online
                            </p>
                            <small className="text-muted">
                              Online payments received
                            </small>
                          </div>
                        </div>
                      </div>

                      {!!summary.totalPending && summary.totalPending > 0 && (
                        <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-3">
                          <div className="card border shadow-sm hover-shadow">
                            <div className="card-body text-center py-4">
                              <div className="mb-3">
                                <i className="ti ti-clock text-warning fs-1"></i>
                              </div>
                              <h2 className="fw-bold mb-1 text-warning">
                                {summary.totalPending?.toLocaleString() || "0"}
                              </h2>
                              <p className="text-muted mb-0 fw-semibold">
                                Total Pending
                              </p>
                              <small className="text-muted">
                                Pending payment amount
                              </small>
                            </div>
                          </div>
                        </div>
                      )}

                      {!!summary.totalDue && summary.totalDue > 0 && (
                        <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-3">
                          <div className="card border shadow-sm hover-shadow">
                            <div className="card-body text-center py-4">
                              <div className="mb-3">
                                <i className="ti ti-alert-triangle text-danger fs-1"></i>
                              </div>
                              <h2 className="fw-bold mb-1 text-danger">
                                {summary.totalDue?.toLocaleString() || "0"}
                              </h2>
                              <p className="text-muted mb-0 fw-semibold">
                                Total Due
                              </p>
                              <small className="text-muted">
                                Overdue payment amount
                              </small>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="card-body p-0 py-3">
              {!hasSearched ? (
                <div className="text-center text-muted py-5">
                  Click Search above to load invoices.
                </div>
              ) : (
                <Table
                  dataSource={invoices}
                  columns={columns}
                  loading={loading || cancellingInvoice}
                  setSearchText={(text: string) => {
                    setHasPendingChanges(true);
                    setSearchText(text);
                  }}
                  pagination={{
                    ...paginationData,
                    onChange: handleChange,
                  }}
                />
              )}
            </div>
          </div>
          <div className="card mb-4">
            <div className="card-header">
              <h4 className="card-title mb-0">Fees Breakdown:</h4>
            </div>

            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading summary...</p>
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  {summary.feeBreakdown.map((item, idx) => (
                    <span
                      key={idx}
                      className="d-inline-flex align-items-baseline gap-2 px-3 py-2 rounded-pill border bg-white shadow-sm"
                    >
                      <span className="text-muted">{item.title}</span>
                      <span className="fw-bold text-primary">
                        Rs {Number(item.amount).toLocaleString()}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="card mb-4">
            <div className="card-header">
              <h4 className="card-title mb-0">Tuition Fee Breakdown</h4>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered mb-0">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Fee Type</th>
                      <th className="text-center">Total Invoices</th>
                      <th className="text-end">Sub Total</th>
                      <th className="text-end">Discount</th>
                      <th className="text-end">Fine</th>
                      <th className="text-end">Paid Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tuitionTotals.tuitionBreakdown.length ? (
                      tuitionTotals.tuitionBreakdown.map((data) => (
                        <tr key={`${data.month}__${data.category}`}>
                          <td>{formatMonth(data.month)}</td>
                          <td>{data.category}</td>
                          <td className="text-center">{data.invoiceIds.size}</td>
                          <td className="text-end">{data.subTotal.toLocaleString()}</td>
                          <td className="text-end">{data.discount.toLocaleString()}</td>
                          <td className="text-end">{data.fine.toLocaleString()}</td>
                          <td className="text-end">{data.paid.toLocaleString()}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center text-muted">
                          No tuition fee data for the selected filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <InvoicesPrintModal
            show={showPrintModal || showExportModal}
            onClose={() => {
              setShowPrintModal(false);
              setShowExportModal(false);
              setAllInvoicesForPrint([]);
            }}
            invoices={allInvoicesForPrint}
            onPrint={() => {
              handlePrint();
            }}
            printRef={printRef}
            filters={{
              startDate: appliedFilters?.startDate,
              endDate: appliedFilters?.endDate,
              system: appliedFilters?.system,
              status: appliedFilters?.status,
              session: currentSession,
            }}
            exportType={exportType}
          />

          <ConfirmationModal
            modalId="delete-invoice-modal"
            title="Confirm Cancellation"
            desc="You want to cancel the selected invoice, this cant be undone once you cancelled it."
            confirmationBtnText="Yes, Delete"
            cancelBtnText="Cancel"
            onConfirm={handleCancelInvoice}
          />
          <InvoicePrintModal
            show={showInvoiceModal}
            onClose={handleCloseInvoiceModal}
            onPrint={handlePrint}
            invoice={selectedInvoice}
            studentDetails={{
              firstName: selectedInvoice?.student?.user.firstName,
              lastName: selectedInvoice?.student?.user.lastName,
              className: selectedInvoice?.class.name,
              grNumber: selectedInvoice?.student?.grNumber,
              section: selectedInvoice?.section?.name,
            }}
            printRef={printRef}
            calculateFinalAmount={calculateFinalAmount}
          />
        </div>
      </div>
    </div>
  );
};

export default AccountsInvoices;
